import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase-config';

// Public Pages
import HomePage from './components/HomePage/HomePage';
import AboutPage from './components/AboutPage';
import PackagesPage from './components/PackagesPage';
import GetStartedPage from './components/GetStartedPage';
import LoginPage from './components/LoginPage';
import SignUpPage from './components/SignUpPage';
import ProposalPage from './components/ProposalPage';
import ThankYouPage from './components/ThankYouPage';
import PrivacyPolicy from './components/PrivacyPolicy';

// Client Portal Pages
import MyAccount from './components/ClientPortal/MyAccount';
import Dashboard from './components/ClientPortal/Dashboard';
import DocumentsUpload from './components/ClientPortal/Upload';
import Launch from './components/ClientPortal/Launch';
import ProjectAcceptance from './components/ClientPortal/ProjectAcceptance';
import CarePlanRequest from './components/ClientPortal/CarePlanRequest';
import StarterIntake from './components/ClientPortal/StarterIntake';
import GrowthIntake from './components/ClientPortal/GrowthIntake';
import AuthorityProcessAudit from './components/ClientPortal/AuthorityProcessAudit';
import BrandQuestionnaire from './components/ClientPortal/BrandQuestionnaire';
import WebContent from './components/ClientPortal/WebContent';
import FAQ from './components/ClientPortal/FAQ';
import HomePageRevision from './components/ClientPortal/HomePageRevision';
import WebsiteRevision from './components/ClientPortal/WebsiteRevision';
import CustomDomainSetup from './components/ClientPortal/CustomDomainSetup';
import Guides from './components/ClientPortal/Guides';
import ClientPortalRevision from './components/ClientPortal/ClientPortalRevision';
import AdminPortalRevision from './components/ClientPortal/AdminPortalRevision';
import TestingQA from './components/ClientPortal/TestingQA';

// Admin Portal Pages
import AdminSignIn from './components/AdminPortal/AdminSignIn';
import AdminDashboard from './components/AdminPortal/AdminDashboard';
import ManageProject from './components/AdminPortal/ManageProject';
import UserDetails from './components/AdminPortal/UserDetails';
import NewProjectWizard from './components/AdminPortal/NewProjectWizard';
import AdminStarterIntake from './components/AdminPortal/AdminStarterIntake';
import AdminGrowthIntake from './components/AdminPortal/AdminGrowthIntake';
import AdminAuthorityProcessAudit from './components/AdminPortal/AdminAuthorityProcessAudit';
import AdminBrandQuestionnaire from './components/AdminPortal/AdminBrandQuestionnaire';
import AdminWebsiteContent from './components/AdminPortal/AdminWebsiteContent';
import AdminUploadedPictures from "./components/AdminPortal/AdminUploadedPictures";
import ProposalCreation from "./components/AdminPortal/ProposalCreation";
import AdminHomePageRevision from './components/AdminPortal/AdminHomePageRevision';
import AdminWebsiteRevision from './components/AdminPortal/AdminWebsiteRevision';
import AdminLaunch from './components/AdminPortal/AdminLaunch';
import AdminCustomDomain from './components/AdminPortal/AdminCustomDomain';
import AdminClientPortalRevision from './components/AdminPortal/AdminClientPortalRevision';
import AdminAdminPortalRevision from './components/AdminPortal/AdminAdminPortalRevision';
import AdminTestingQA from './components/AdminPortal/AdminTestingQA';
import AdminCarePlanRequests from './components/AdminPortal/AdminCarePlanRequests';
import AdminCarePlanRequestDetail from './components/AdminPortal/AdminCarePlanRequestDetail';
import CarePlanRequestDetail from './components/ClientPortal/CarePlanRequestDetail';

// Shared Components
import Layout from './components/shared/Layout';
import ProtectedRoute from './utils/ProtectedRoute';
import ScrollToTop from './components/shared/ScrollToTop';

// StyleSheet
import './App.css';

// Toast
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function App() {
  const [user, setUser] = useState(null); // Track user authentication state

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || null); // Set user or null if not authenticated
    });

    return () => unsubscribe();
  }, []);

  return (
    <>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route element={<Layout layoutType="public" user={user} />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/packages" element={<PackagesPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/proposal/:projectId" element={<ProposalPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Route>

          {/* Client Portal Routes (Protected) */}
          <Route element={<Layout layoutType="client" user={user} />}>
            <Route
              path="/client"
              element={
                <ProtectedRoute user={user}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/upload"
              element={
                <ProtectedRoute user={user}>
                  <DocumentsUpload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/my-account"
              element={
                <ProtectedRoute user={user}>
                  <MyAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/project-acceptance"
              element={
                <ProtectedRoute user={user}>
                  <ProjectAcceptance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/care-plan-request"
              element={
                <ProtectedRoute user={user}>
                  <CarePlanRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/care-plan-request/:requestId"
              element={
                <ProtectedRoute user={user}>
                  <CarePlanRequestDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/starter-intake"
              element={
                <ProtectedRoute user={user}>
                  <StarterIntake />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/growth-intake"
              element={
                <ProtectedRoute user={user}>
                  <GrowthIntake />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/process-audit"
              element={
                <ProtectedRoute user={user}>
                  <AuthorityProcessAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/brand-questionnaire"
              element={
                <ProtectedRoute user={user}>
                  <BrandQuestionnaire />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/web-content"
              element={
                <ProtectedRoute user={user}>
                  <WebContent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/faqs"
              element={
                <ProtectedRoute user={user}>
                  <FAQ />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/guides"
              element={
                <ProtectedRoute user={user}>
                  <Guides />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/homepage-revision"
              element={
                <ProtectedRoute user={user}>
                  <HomePageRevision />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/website-revision"
              element={
                <ProtectedRoute user={user}>
                  <WebsiteRevision />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/domain"
              element={
                <ProtectedRoute user={user}>
                  <CustomDomainSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/launch"
              element={
                <ProtectedRoute user={user}>
                  <Launch />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/client-portal-revision"
              element={
                <ProtectedRoute user={user}>
                  <ClientPortalRevision />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/admin-portal-revision"
              element={
                <ProtectedRoute user={user}>
                  <AdminPortalRevision />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/testing-qa"
              element={
                <ProtectedRoute user={user}>
                  <TestingQA />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Admin Portal Routes (Protected) */}
          <Route element={<Layout layoutType="admin" user={user} />}>
            <Route
              path="/admin"
              element={
                <ProtectedRoute user={user}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId"
              element={
                <ProtectedRoute user={user}>
                  <ManageProject />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/user/:userId"
              element={
                <ProtectedRoute user={user}>
                  <UserDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/new-project"
              element={
                <ProtectedRoute user={user}>
                  <NewProjectWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/care-plan-requests"
              element={
                <ProtectedRoute user={user}>
                  <AdminCarePlanRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/care-plan-requests/:requestId"
              element={
                <ProtectedRoute user={user}>
                  <AdminCarePlanRequestDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/starter-intake"
              element={
                <ProtectedRoute user={user}>
                  <AdminStarterIntake />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/growth-intake"
              element={
                <ProtectedRoute user={user}>
                  <AdminGrowthIntake />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/process-audit"
              element={
                <ProtectedRoute user={user}>
                  <AdminAuthorityProcessAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/brand-questionnaire"
              element={
                <ProtectedRoute user={user}>
                  <AdminBrandQuestionnaire />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/website-content"
              element={
                <ProtectedRoute user={user}>
                  <AdminWebsiteContent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/uploaded-pictures"
              element={
                <ProtectedRoute user={user}>
                  <AdminUploadedPictures />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/create-proposal"
              element={
                <ProtectedRoute user={user}>
                  <ProposalCreation />
                </ProtectedRoute>
              } />
            <Route
              path="/admin/project/:projectId/homepage-revision"
              element={
                <ProtectedRoute user={user}>
                  <AdminHomePageRevision />
                </ProtectedRoute>
              } />
            <Route
              path="/admin/project/:projectId/website-revision"
              element={
                <ProtectedRoute user={user}>
                  <AdminWebsiteRevision />
                </ProtectedRoute>
              } />
              <Route
              path="/admin/project/:projectId/launch"
              element={
                <ProtectedRoute user={user}>
                  <AdminLaunch />
                </ProtectedRoute>
              } />
              <Route
              path="/admin/project/:projectId/domain"
              element={
                <ProtectedRoute user={user}>
                  <AdminCustomDomain />
                </ProtectedRoute>
              } />
            <Route
              path="/admin/project/:projectId/client-portal-revision"
              element={
                <ProtectedRoute user={user}>
                  <AdminClientPortalRevision />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/project/:projectId/admin-portal-revision"
              element={
                <ProtectedRoute user={user}>
                  <AdminAdminPortalRevision />
                </ProtectedRoute>
              }
            />
          <Route
            path="/admin/project/:projectId/testing-qa"
            element={
              <ProtectedRoute user={user}>
                <AdminTestingQA />
              </ProtectedRoute>
            }
          />
            <Route path="/admin/login" element={<AdminSignIn />} />
          </Route>
        </Routes>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
}

export default App;
